import { useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import Button from "Components/Common/Button/Button";
import Input from "Components/Common/Input/Input";
import { useToast } from "Components/Common/Toast/ToastContext";
import type { ChangePasswordPayload } from "services/profileService";
import OpenEye from "Assets/Img/OpenEye";
import CloseEye from "Assets/Img/CloseEye";

interface StrengthLevel {
  width: string;
  color: string;
  label: string;
}

function calcStrength(pw: string): StrengthLevel {
  if (!pw) {
    return {
      width: "0%",
      color: "var(--tx3)",
      label: "Enter a password to check strength",
    };
  }
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[a-z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;

  const levels: StrengthLevel[] = [
    { width: "16%", color: "var(--red)", label: "Very weak" },
    { width: "16%", color: "var(--red)", label: "Weak" },
    { width: "33%", color: "var(--amber)", label: "Fair" },
    { width: "50%", color: "var(--amber)", label: "Good" },
    { width: "75%", color: "#22c55e", label: "Strong" },
    { width: "90%", color: "#16a34a", label: "Very strong" },
    { width: "100%", color: "#16a34a", label: "Excellent" },
  ];
  return levels[Math.min(score, levels.length - 1)];
}

const PwSchema = Yup.object({
  currentPassword: Yup.string().required("Current password is required"),
  newPassword: Yup.string()
    .required("New password is required")
    .min(12, "Password must be at least 12 characters")
    .matches(/[A-Z]/, "Must contain an uppercase letter")
    .matches(/[a-z]/, "Must contain a lowercase letter")
    .matches(/[0-9]/, "Must contain a number")
    .matches(/[^A-Za-z0-9]/, "Must contain a special character"),
  confirmPassword: Yup.string()
    .required("Please confirm your password")
    .oneOf([Yup.ref("newPassword")], "Passwords do not match"),
});

type PwField = "currentPassword" | "newPassword" | "confirmPassword";

interface Props {
  onChangePassword: (data: ChangePasswordPayload) => Promise<boolean>;
  onToggleSms2fa: (enabled: boolean) => Promise<boolean>;
  onToggleEmailAlert: (enabled: boolean) => Promise<boolean>;
  changing: boolean;
  sms2faEnabled?: boolean;
  emailLoginAlertEnabled?: boolean;
  smsNotificationsEnabled?: boolean;
}

export default function PasswordSecuritySection({
  onChangePassword,
  onToggleSms2fa,
  onToggleEmailAlert,
  changing,
  sms2faEnabled = true,
  emailLoginAlertEnabled = true,
  smsNotificationsEnabled = false,
}: Props) {
  const showToast = useToast();
  const [pwVis, setPwVis] = useState<Record<PwField, boolean>>({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [smsAuth, setSmsAuth] = useState(sms2faEnabled);
  const [emailAuth, setEmailAuth] = useState(emailLoginAlertEnabled);

  const toggleVis = (field: PwField) =>
    setPwVis((v) => ({ ...v, [field]: !v[field] }));

  return (
    <div>
      <div className="pf-section-head">
        <h2>Password &amp; Security</h2>
        <p>Manage your password and account security settings.</p>
      </div>

      <Formik
        initialValues={{
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        }}
        validationSchema={PwSchema}
        onSubmit={async (values, { resetForm }) => {
          const ok = await onChangePassword({
            currentPassword: values.currentPassword,
            newPassword: values.newPassword,
          });
          if (ok) {
            resetForm();
            setPwVis({
              currentPassword: false,
              newPassword: false,
              confirmPassword: false,
            });
          }
        }}
      >
        {({ values }) => {
          const strength = calcStrength(values.newPassword);
          return (
            <Form>
              <div className="pf-card">
                <div className="pf-card__title">Change Password</div>

                <Field name="currentPassword">
                  {({ field, meta }: any) => (
                    <div className="pf-field">
                      <label>
                        Current Password <span className="pf-req">*</span>
                      </label>
                      <div className="pf-pw-wrap">
                        <Input
                          type={pwVis.currentPassword ? "text" : "password"}
                          placeholder="Enter current password"
                          className="pf-fi"
                          isError={meta.touched && !!meta.error}
                          {...field}
                        />
                        <Button
                          type="button"
                          className="pf-pw-toggle"
                          onClick={() => toggleVis("currentPassword")}
                        >
                          {pwVis.currentPassword ? <OpenEye /> : <CloseEye />}
                        </Button>
                      </div>
                      {meta.touched && meta.error && (
                        <p className="pf-field-error">{meta.error}</p>
                      )}
                    </div>
                  )}
                </Field>

                <Field name="newPassword">
                  {({ field, meta }: any) => (
                    <div className="pf-field">
                      <label>
                        New Password <span className="pf-req">*</span>
                      </label>
                      <div className="pf-pw-wrap">
                        <Input
                          type={pwVis.newPassword ? "text" : "password"}
                          className="pf-fi"
                          placeholder="Enter new password"
                          isError={meta.touched && !!meta.error}
                          {...field}
                        />
                        <Button
                          type="button"
                          className="pf-pw-toggle"
                          onClick={() => toggleVis("newPassword")}
                        >
                          {pwVis.newPassword ? <OpenEye /> : <CloseEye />}
                        </Button>
                      </div>
                      <div className="pf-pw-strength">
                        <div
                          className="pf-pw-strength__fill"
                          style={{
                            width: values.newPassword ? strength.width : "0%",
                            background: values.newPassword
                              ? strength.color
                              : "transparent",
                          }}
                        />
                      </div>
                      <div
                        className="pf-pw-strength-label"
                        style={{
                          color: values.newPassword
                            ? strength.color
                            : "var(--tx3)",
                        }}
                      >
                        {strength.label}
                      </div>
                      <div className="pf-field-help">
                        Minimum 12 characters. Must include uppercase,
                        lowercase, number, and special character.
                      </div>
                      {meta.touched && meta.error && (
                        <p className="pf-field-error">{meta.error}</p>
                      )}
                    </div>
                  )}
                </Field>

                <Field name="confirmPassword">
                  {({ field, meta }: any) => (
                    <div className="pf-field">
                      <label>
                        Confirm New Password <span className="pf-req">*</span>
                      </label>
                      <div className="pf-pw-wrap">
                        <Input
                          type={pwVis.confirmPassword ? "text" : "password"}
                          placeholder="Re-enter new password"
                          className="pf-fi"
                          isError={meta.touched && !!meta.error}
                          {...field}
                        />
                        <Button
                          type="button"
                          className="pf-pw-toggle"
                          onClick={() => toggleVis("confirmPassword")}
                        >
                          {pwVis.confirmPassword ? <OpenEye /> : <CloseEye />}
                        </Button>
                      </div>
                      {meta.touched && meta.error && (
                        <p className="pf-field-error">{meta.error}</p>
                      )}
                    </div>
                  )}
                </Field>

                <div className="pf-btn-row">
                  <Button
                    type="submit"
                    className="pf-btn-primary"
                    disabled={changing}
                  >
                    {changing ? (
                      <>
                        <span className="btn-spinner" /> Updating…
                      </>
                    ) : (
                      "Update Password"
                    )}
                  </Button>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>

      <div className="pf-card">
        <div className="pf-card__title">Two-Factor Authentication</div>
        <div className="pf-toggle-row">
          <div className="pf-tr-info">
            <div className="pf-tr-info__label">
              SMS Two-Factor Authentication
            </div>
            <div className="pf-tr-info__sub">
              Receive a verification code via SMS when logging in from a new
              device. Requires a verified phone number.
            </div>
          </div>
          <label className="pf-toggle">
            <Input
              type="checkbox"
              checked={smsAuth}
              onChange={() => {
                const next = !smsAuth;
                if (next && !smsNotificationsEnabled) {
                  showToast(
                    "Please enable SMS Notifications before enabling SMS Two-Factor Authentication.",
                  );
                  return;
                }
                setSmsAuth(next);
                void onToggleSms2fa(next).then((ok) => {
                  if (!ok) setSmsAuth((v) => !v);
                });
              }}
            />
            <span className="pf-toggle__track" />
          </label>
        </div>
        <div className="pf-toggle-row">
          <div className="pf-tr-info">
            <div className="pf-tr-info__label">
              Email Verification on New Login
            </div>
            <div className="pf-tr-info__sub">
              Receive an email confirmation when your account is accessed from a
              new browser or device.
            </div>
          </div>
          <label className="pf-toggle">
            <Input
              type="checkbox"
              checked={emailAuth}
              onChange={() => {
                const next = !emailAuth;
                setEmailAuth(next);
                void onToggleEmailAlert(next).then((ok) => {
                  if (!ok) setEmailAuth((v) => !v);
                });
              }}
            />
            <span className="pf-toggle__track" />
          </label>
        </div>
      </div>
    </div>
  );
}
